import { CardDetailArt } from "./CardDetailArt";
import { CardSyntheticFace } from "./CardSyntheticFace";
import { DeckOverlayButtons } from "./DeckOverlayButtons";
import { StatPill } from "./StatPill";
import { revisedPackLabel } from "../utils/revisedPackNames";

export function DetailModal({ card, onClose, signedIn, deckQuantity, onDeckAdd, onDeckRemove }) {
  if (!card) return null;

  const qty = deckQuantity ?? 0;
  const packNames =
    card.pack_names?.length > 0
      ? card.pack_names
      : card.pack_name
        ? [card.pack_name]
        : [];
  const revisedPacks = [
    ...new Set(packNames.map((name) => revisedPackLabel(name)).filter(Boolean)),
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="card-detail-title"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
    >
      <div
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl border border-slate-600 bg-slate-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-4 p-6 md:flex-row md:items-start">
          <div className="relative mx-auto w-full max-w-sm shrink-0 md:mx-0">
            {card.image_url ? (
              <>
                <CardDetailArt src={card.image_url} alt="" card={card} />
                <DeckOverlayButtons
                  quantity={qty}
                  signedIn={signedIn}
                  onAdd={onDeckAdd}
                  onRemove={onDeckRemove}
                />
              </>
            ) : (
              <CardSyntheticFace card={card} variant="detail">
                <DeckOverlayButtons
                  quantity={qty}
                  signedIn={signedIn}
                  onAdd={onDeckAdd}
                  onRemove={onDeckRemove}
                />
              </CardSyntheticFace>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h2 id="card-detail-title" className="text-2xl font-bold text-slate-50">
                {card.name}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-slate-600 px-3 py-1 text-sm text-slate-300 hover:bg-slate-800"
              >
                Close
              </button>
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              {card.type_name && <StatPill>{card.type_name}</StatPill>}
              {card.sphere_name && <StatPill>{card.sphere_name}</StatPill>}
              {(card.cost != null && card.cost !== "") && <StatPill>Cost {card.cost}</StatPill>}
              {(card.threat != null && card.threat !== "") && <StatPill>Threat {card.threat}</StatPill>}
              {card.is_unique && <StatPill>Unique</StatPill>}
              {(card.deck_limit != null && card.deck_limit !== "") && (
                <StatPill>Deck limit {card.deck_limit}</StatPill>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
              {[
                ["Willpower", card.willpower],
                ["Attack", card.attack],
                ["Defense", card.defense],
                ["Health", card.health],
              ].map(([label, val]) =>
                val != null && val !== "" ? (
                  <div key={label}>
                    <dt className="text-slate-500">{label}</dt>
                    <dd className="font-medium text-slate-200">{String(val)}</dd>
                  </div>
                ) : null
              )}
              {packNames.length > 0 ? (
                <div className="sm:col-span-3">
                  <dt className="text-slate-500">{packNames.length === 1 ? "Pack" : "Packs"}</dt>
                  <dd className="flex flex-wrap gap-1.5 font-medium text-slate-200">
                    {packNames.map((name) => (
                      <span
                        key={name}
                        className="rounded-full border border-slate-600 bg-slate-800/70 px-2 py-0.5 text-xs"
                      >
                        {name}
                      </span>
                    ))}
                  </dd>
                </div>
              ) : null}
              {revisedPacks.length > 0 ? (
                <div className="sm:col-span-3">
                  <dt className="text-slate-500">Revised content</dt>
                  <dd className="flex flex-wrap gap-1.5 font-medium text-amber-100/90">
                    {revisedPacks.map((name) => (
                      <span
                        key={name}
                        className="rounded-full border border-amber-300/30 bg-amber-500/10 px-2 py-0.5 text-xs"
                      >
                        {name}
                      </span>
                    ))}
                  </dd>
                </div>
              ) : null}
            </dl>
            {card.traits && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Traits</h4>
                <p className="text-slate-300">{card.traits}</p>
              </div>
            )}
            {card.keywords && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Keywords</h4>
                <p className="text-amber-200/90">{card.keywords}</p>
              </div>
            )}
            {card.text && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Text</h4>
                <div
                  className="prose prose-invert max-w-none text-sm prose-p:my-1"
                  dangerouslySetInnerHTML={{ __html: card.text }}
                />
              </div>
            )}
            {card.flavor && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Flavor</h4>
                <div
                  className="italic text-slate-400"
                  dangerouslySetInnerHTML={{ __html: card.flavor }}
                />
              </div>
            )}
            {card.url && (
              <a
                href={card.url}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-sm text-amber-400 hover:underline"
              >
                View on RingsDB
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
